/**
 * SoleadLab 2.0 - UI Store
 *
 * Manages UI state including panel visibility, modals, and notifications.
 */

import { create } from 'zustand';
import type { IndicationType, IndicationStatus } from './projectStore';

/**
 * Panel IDs
 */
export type PanelId =
  | 'leftPanel'
  | 'rightPanel'
  | 'layersPanel'
  | 'adminPanel'
  | 'mobileUploadsPanel'
  | 'alarmsPanel'
  | 'tasksPanel'
  | 'calculatorPanel'
  | 'ivPanel'
  | 'statePanel'
  | 'scoringPanel'
  | 'copilotPanel'
  | 'photoRegPanel'
  | 'photoClassifierPanel'
  | 'teamPanel'
  | 'orgSettingsPanel'
  | 'shareLinksPanel';

/**
 * Modal IDs
 */
export type ModalId =
  | 'addIndication'
  | 'editIndication'
  | 'editDataPoint'
  | 'uploadSchema'
  | 'uploadData'
  | 'apiSettings'
  | 'apiConfig'
  | 'scadaSettings'
  | 'sldViewer'
  | 'flightPathDetails'
  | 'photoViewer'
  | 'exportOptions'
  | 'shareLink'
  | 'settings'
  | 'confirmDialog'
  | 'rotateOrthophoto'
  | 'advancedRule'
  | 'driveImport'
  | 'storageAdmin'
  | 'layoutImport'
  | 'classificationConfig';

/**
 * Notification type
 */
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Confirm dialog options
 */
export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  onConfirm: () => void;
  onCancel?: () => void;
}

/**
 * Context menu item
 */
export interface ContextMenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
}

interface UIState {
  // Panel states
  panelStates: Record<PanelId, { isOpen: boolean; isCollapsed: boolean }>;

  // Modal states
  activeModal: ModalId | null;
  modalData: Record<string, unknown>;

  // Confirm dialog
  confirmDialog: ConfirmDialogOptions | null;

  // Notifications
  notifications: Notification[];

  // Loading states
  isLoading: boolean;
  loadingMessage: string | null;

  // Context menu
  contextMenu: {
    x: number;
    y: number;
    items: ContextMenuItem[];
  } | null;

  // Active nav-rail ticket drawers
  leftActiveTicket: string | null;
  rightActiveTicket: string | null;

  // Add mode (for indication creation)
  isAddMode: boolean;
  addModeType: string | null;

  /** Quick-add: pre-selected indication type — armed map clicks create this
   *  type immediately with no popup. null ⇒ ask each time (popup flow). Only
   *  meaningful while isAddMode && addModeType === null; cleared on disarm. */
  quickAddIndicationType: IndicationType | null;

  /** Batch status paint mode: while armed, clicking an indication marker
   *  applies batchStatusTarget to it immediately (no preview popup).
   *  Mutually exclusive with add mode — arming one disarms the other. */
  batchStatusMode: boolean;

  /** Target for paint mode AND apply-to-selected. Deliberately persists across
   *  disarm (unlike quickAddIndicationType): the select stays visible in the
   *  ticket, status changes are idempotent/reversible, and apply-to-selected
   *  uses it while disarmed. */
  batchStatusTarget: IndicationStatus;

  // Default orientation for new indications (degrees)
  defaultIndicationOrientation: number;

  /** Observation time for the next manually-placed indications (datetime-local
   *  "YYYY-MM-DDTHH:MM"), stamped as createdAt + capture day. null ⇒ use now. */
  defaultObservationTime: string | null;

  // Default module count for String OC indications
  defaultStringModules: number;

  // Asset place mode callback (for mobile uploads → map click placement)
  assetPlaceCallback: ((lngLat: { lng: number; lat: number }) => void) | null;
  setAssetPlaceCallback: (cb: ((lngLat: { lng: number; lat: number }) => void) | null) => void;

  /** FL-3a Flow C: a photo waiting to attach to the NEXT field quick-add
   *  create (photo-first reporting; also the no-GPS fallback). Cleared by
   *  the attach flow (utils/fieldPhotoFlow). */
  pendingFieldPhoto: { file: File; name: string } | null;
  setPendingFieldPhoto: (pending: { file: File; name: string } | null) => void;

  /** FL-3a: indication id with an auto-attach upload in flight (Flow C).
   *  The Field update card disables its writes for this id so a status tap
   *  can't 409 against the append (review finding). */
  fieldPhotoUploading: string | null;
  setFieldPhotoUploading: (id: string | null) => void;

  // Layout anchor mode (split-screen PDF ↔ Map)
  layoutAnchorMode: boolean;
  setLayoutAnchorMode: (enabled: boolean) => void;

  // Overlay anchor registration mode (split-screen image ↔ Map)
  overlayAnchorMode: string | null; // overlay ID or null
  setOverlayAnchorMode: (overlayId: string | null) => void;

  // Photo anchor registration mode (Tier 3 split-screen)
  photoAnchorMode: boolean;
  setPhotoAnchorMode: (enabled: boolean) => void;

  // Layout label editing
  selectedLayoutEntityId: string | null;
  selectedLayoutEntityIds: string[];
  layoutLabelMoveMode: boolean;
  layoutLabelAddMode: boolean;
  layoutLabelSelectMode: boolean;
  /** Area-select whole module GRIDS (strings) for batch move/edit — mutually
   *  exclusive with layoutLabelSelectMode (owner request 2026-09-02). */
  layoutGridSelectMode: boolean;
  newLabelPosition: { lng: number; lat: number } | null;
  selectLayoutEntity: (id: string | null) => void;
  selectLayoutEntities: (ids: string[]) => void;
  toggleLayoutEntitySelection: (id: string) => void;
  setLayoutLabelMoveMode: (enabled: boolean) => void;
  setLayoutLabelAddMode: (enabled: boolean) => void;
  setLayoutLabelSelectMode: (enabled: boolean) => void;
  setLayoutGridSelectMode: (enabled: boolean) => void;
  setNewLabelPosition: (pos: { lng: number; lat: number } | null) => void;

  // Progress overlay (save/open operations)
  progressOverlay: { visible: boolean; label: string; current: number; total: number } | null;
  setProgressOverlay: (p: { visible: boolean; label: string; current: number; total: number } | null) => void;

  // Project selection modal
  showProjectSelect: boolean;
  setShowProjectSelect: (show: boolean) => void;

  // Panel actions
  openPanel: (id: PanelId) => void;
  closePanel: (id: PanelId) => void;
  togglePanel: (id: PanelId) => void;
  collapsePanel: (id: PanelId) => void;
  expandPanel: (id: PanelId) => void;
  togglePanelCollapse: (id: PanelId) => void;

  // Modal actions
  openModal: (id: ModalId, data?: Record<string, unknown>) => void;
  closeModal: () => void;

  // Confirm dialog actions
  showConfirm: (options: ConfirmDialogOptions) => void;
  hideConfirm: () => void;

  // Notification actions
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Loading actions
  setLoading: (loading: boolean, message?: string) => void;

  // Context menu actions
  showContextMenu: (x: number, y: number, items: ContextMenuItem[]) => void;
  hideContextMenu: () => void;

  // Ticket drawer actions
  setLeftActiveTicket: (id: string | null) => void;
  setRightActiveTicket: (id: string | null) => void;

  // Add mode actions
  setAddMode: (enabled: boolean, type?: string) => void;
  toggleAddMode: () => void;
  setQuickAddIndicationType: (type: IndicationType | null) => void;

  // Batch status paint mode actions
  setBatchStatusMode: (enabled: boolean) => void;
  setBatchStatusTarget: (target: IndicationStatus) => void;

  // Orientation actions
  setDefaultIndicationOrientation: (degrees: number) => void;
  setDefaultObservationTime: (value: string | null) => void;

  // String module count actions
  setDefaultStringModules: (count: number) => void;
}

const defaultPanelStates: Record<PanelId, { isOpen: boolean; isCollapsed: boolean }> = {
  leftPanel: { isOpen: true, isCollapsed: false },
  rightPanel: { isOpen: true, isCollapsed: false },
  layersPanel: { isOpen: true, isCollapsed: false },
  adminPanel: { isOpen: false, isCollapsed: false },
  mobileUploadsPanel: { isOpen: false, isCollapsed: false },
  alarmsPanel: { isOpen: false, isCollapsed: false },
  tasksPanel: { isOpen: false, isCollapsed: false },
  calculatorPanel: { isOpen: false, isCollapsed: false },
  ivPanel: { isOpen: false, isCollapsed: false },
  statePanel: { isOpen: false, isCollapsed: false },
  scoringPanel: { isOpen: false, isCollapsed: false },
  copilotPanel: { isOpen: false, isCollapsed: false },
  photoRegPanel: { isOpen: false, isCollapsed: false },
  photoClassifierPanel: { isOpen: false, isCollapsed: false },
  teamPanel: { isOpen: false, isCollapsed: false },
  orgSettingsPanel: { isOpen: false, isCollapsed: false },
  shareLinksPanel: { isOpen: false, isCollapsed: false },
};

let notificationId = 0;

export const useUIStore = create<UIState>((set, get) => ({
  panelStates: defaultPanelStates,
  activeModal: null,
  modalData: {},
  confirmDialog: null,
  notifications: [],
  isLoading: false,
  loadingMessage: null,
  contextMenu: null,
  leftActiveTicket: null,
  rightActiveTicket: null,
  isAddMode: false,
  addModeType: null,
  quickAddIndicationType: null,
  batchStatusMode: false,
  batchStatusTarget: 'resolved',
  defaultIndicationOrientation: 0,
  defaultObservationTime: null,
  defaultStringModules: 20,
  assetPlaceCallback: null,
  setAssetPlaceCallback: (cb) => set({ assetPlaceCallback: cb }),

  pendingFieldPhoto: null,
  setPendingFieldPhoto: (pending) => set({ pendingFieldPhoto: pending }),

  fieldPhotoUploading: null,
  setFieldPhotoUploading: (id) => set({ fieldPhotoUploading: id }),
  layoutAnchorMode: false,
  setLayoutAnchorMode: (enabled) => set({ layoutAnchorMode: enabled }),
  overlayAnchorMode: null,
  setOverlayAnchorMode: (overlayId) => set({ overlayAnchorMode: overlayId }),
  photoAnchorMode: false,
  setPhotoAnchorMode: (enabled) => set({ photoAnchorMode: enabled }),
  selectedLayoutEntityId: null,
  selectedLayoutEntityIds: [],
  layoutLabelMoveMode: false,
  layoutLabelAddMode: false,
  layoutLabelSelectMode: false,
  layoutGridSelectMode: false,
  newLabelPosition: null,
  selectLayoutEntity: (id) => set({
    selectedLayoutEntityId: id,
    selectedLayoutEntityIds: id ? [id] : [],
    layoutLabelMoveMode: false,
  }),
  selectLayoutEntities: (ids) => set({
    selectedLayoutEntityIds: ids,
    selectedLayoutEntityId: ids.length === 1 ? ids[0] : null,
  }),
  toggleLayoutEntitySelection: (id) => set((state) => {
    const prev = state.selectedLayoutEntityIds;
    const next = prev.includes(id)
      ? prev.filter((x) => x !== id)
      : [...prev, id];
    return {
      selectedLayoutEntityIds: next,
      selectedLayoutEntityId: next.length === 1 ? next[0] : null,
    };
  }),
  setLayoutLabelMoveMode: (enabled) => set({ layoutLabelMoveMode: enabled }),
  setLayoutLabelAddMode: (enabled) => set({
    layoutLabelAddMode: enabled,
    newLabelPosition: null,
    selectedLayoutEntityId: null,
    selectedLayoutEntityIds: [],
  }),
  setLayoutLabelSelectMode: (enabled) => set({
    layoutLabelSelectMode: enabled,
    layoutGridSelectMode: false,
    selectedLayoutEntityIds: enabled ? [] : get().selectedLayoutEntityIds,
  }),
  setLayoutGridSelectMode: (enabled) => set({
    layoutGridSelectMode: enabled,
    layoutLabelSelectMode: false,
    selectedLayoutEntityIds: enabled ? [] : get().selectedLayoutEntityIds,
  }),
  setNewLabelPosition: (pos) => set({ newLabelPosition: pos }),
  progressOverlay: null,
  setProgressOverlay: (p) => set({ progressOverlay: p }),
  showProjectSelect: false,
  setShowProjectSelect: (show) => set({ showProjectSelect: show }),

  // Panel actions
  openPanel: (id) =>
    set((state) => ({
      panelStates: {
        ...state.panelStates,
        [id]: { ...state.panelStates[id], isOpen: true },
      },
    })),

  closePanel: (id) =>
    set((state) => ({
      panelStates: {
        ...state.panelStates,
        [id]: { ...state.panelStates[id], isOpen: false },
      },
    })),

  togglePanel: (id) =>
    set((state) => ({
      panelStates: {
        ...state.panelStates,
        [id]: { ...state.panelStates[id], isOpen: !state.panelStates[id].isOpen },
      },
    })),

  collapsePanel: (id) =>
    set((state) => ({
      panelStates: {
        ...state.panelStates,
        [id]: { ...state.panelStates[id], isCollapsed: true },
      },
    })),

  expandPanel: (id) =>
    set((state) => ({
      panelStates: {
        ...state.panelStates,
        [id]: { ...state.panelStates[id], isCollapsed: false },
      },
    })),

  togglePanelCollapse: (id) =>
    set((state) => ({
      panelStates: {
        ...state.panelStates,
        [id]: { ...state.panelStates[id], isCollapsed: !state.panelStates[id].isCollapsed },
      },
    })),

  // Modal actions
  openModal: (id, data = {}) =>
    set({
      activeModal: id,
      modalData: data,
    }),

  closeModal: () =>
    set({
      activeModal: null,
      modalData: {},
    }),

  // Confirm dialog actions
  showConfirm: (options) => set({ confirmDialog: options }),
  hideConfirm: () => set({ confirmDialog: null }),

  // Notification actions
  addNotification: (notification) => {
    const id = `notification-${++notificationId}`;
    const newNotification: Notification = { ...notification, id };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove after duration
    const duration = notification.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, duration);
    }
  },

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearNotifications: () => set({ notifications: [] }),

  // Loading actions
  setLoading: (loading, message) =>
    set({
      isLoading: loading,
      loadingMessage: loading ? (message ?? null) : null,
    }),

  // Context menu actions
  showContextMenu: (x, y, items) =>
    set({
      contextMenu: { x, y, items },
    }),

  hideContextMenu: () => set({ contextMenu: null }),

  // Ticket drawer actions
  setLeftActiveTicket: (id) =>
    set((state) => ({ leftActiveTicket: state.leftActiveTicket === id ? null : id })),
  setRightActiveTicket: (id) =>
    set((state) => ({ rightActiveTicket: state.rightActiveTicket === id ? null : id })),

  // Add mode actions
  // Disarming — or arming a typed mode like dataPoint — always clears the
  // quick-add type, so re-arming starts at "ask each time" (popup flow).
  // Arming also disarms batch status paint mode: one armed map mode at a time.
  setAddMode: (enabled, type) =>
    set((state) => ({
      isAddMode: enabled,
      addModeType: enabled ? (type ?? null) : null,
      quickAddIndicationType: enabled && !type ? state.quickAddIndicationType : null,
      batchStatusMode: enabled ? false : state.batchStatusMode,
    })),

  toggleAddMode: () =>
    set((state) => ({
      isAddMode: !state.isAddMode,
      addModeType: state.isAddMode ? null : state.addModeType,
      quickAddIndicationType: state.isAddMode ? null : state.quickAddIndicationType,
      batchStatusMode: state.isAddMode ? state.batchStatusMode : false,
    })),

  setQuickAddIndicationType: (type) => set({ quickAddIndicationType: type }),

  // Batch status paint mode actions
  // Arming disarms add mode (both variants) — one armed map mode at a time.
  // batchStatusTarget intentionally survives disarm (see state doc above).
  setBatchStatusMode: (enabled) =>
    set((state) => ({
      batchStatusMode: enabled,
      isAddMode: enabled ? false : state.isAddMode,
      addModeType: enabled ? null : state.addModeType,
      quickAddIndicationType: enabled ? null : state.quickAddIndicationType,
    })),

  setBatchStatusTarget: (target) => set({ batchStatusTarget: target }),

  // Orientation actions
  setDefaultIndicationOrientation: (degrees) => set({ defaultIndicationOrientation: degrees }),
  setDefaultObservationTime: (value) => set({ defaultObservationTime: value || null }),

  // String module count actions
  setDefaultStringModules: (count) => set({ defaultStringModules: Math.max(1, count) }),
}));
